// Toast notification component with progress bar, close button, and swipe gesture
// UPDATED: Fixed progress bar pause/resume using CSS animation-play-state for faithful visual representation
// Moved progress bar to top, improved accessibility (conditional aria-live), refined shadow and exit animation
// UPDATED: Added 'warning' toast type with amber styling for non-blocking propagation feedback
// UPDATED: Fixed ARIA semantics — errors use role="alert" (assertive), non-errors use role="status" (polite)

import { useState, useRef, useEffect, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
  onClose: (id: string) => void;
}

const ICON_MAP = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle
};

const COLOR_MAP = {
  success: {
    icon: 'text-emerald-600',
    progress: 'bg-emerald-500'
  },
  error: {
    icon: 'text-red-600',
    progress: 'bg-red-500'
  },
  info: {
    icon: 'text-blue-600',
    progress: 'bg-blue-500'
  },
  warning: {
    icon: 'text-amber-600',
    progress: 'bg-amber-500'
  }
};

const ARIA_LIVE_MAP: Record<ToastType, 'assertive' | 'polite'> = {
  error: 'assertive',
  success: 'polite',
  info: 'polite',
  warning: 'polite'
};

const ROLE_MAP: Record<ToastType, 'alert' | 'status'> = {
  error: 'alert',
  success: 'status',
  info: 'status',
  warning: 'status'
};

export function Toast({ id, message, type, duration, onClose }: ToastProps) {
  const [isPaused, setIsPaused] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);

  const remainingRef = useRef(duration);
  const startTimeRef = useRef(Date.now());
  const timeoutRef = useRef<number>();
  const touchStartXRef = useRef(0);
  const touchCurrentXRef = useRef(0);

  const Icon = ICON_MAP[type];
  const colors = COLOR_MAP[type];

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onClose(id), 200);
  }, [id, onClose]);

  const pause = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const elapsed = Date.now() - startTimeRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    startTimeRef.current = Date.now();
    setIsPaused(false);
  }, []);

  useEffect(() => {
    if (!isPaused) {
      timeoutRef.current = window.setTimeout(handleClose, remainingRef.current);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isPaused, handleClose]);

  const handleMouseEnter = () => {
    if (!isPaused) pause();
  };

  const handleMouseLeave = () => {
    if (isPaused) resume();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    if (!isPaused) pause();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchCurrentXRef.current = e.touches[0].clientX;
    const diff = touchCurrentXRef.current - touchStartXRef.current;
    if (diff > 0) setSwipeOffset(diff);
  };

  const handleTouchEnd = () => {
    const diff = touchCurrentXRef.current - touchStartXRef.current;
    if (diff > 100) {
      handleClose();
    } else {
      setSwipeOffset(0);
      if (isPaused) resume();
    }
    touchStartXRef.current = 0;
    touchCurrentXRef.current = 0;
  };

  return (
    <div
      className={`
        bg-white border border-gray-200 rounded-lg overflow-hidden
        transition-all duration-200 ease-out
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
        ${!isExiting && swipeOffset === 0 ? 'animate-slide-in-right' : ''}
      `}
      style={{
        transform: swipeOffset > 0 ? `translateX(${swipeOffset}px)` : undefined,
        width: '360px',
        maxWidth: 'calc(100vw - 32px)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role={ROLE_MAP[type]}
      aria-live={ARIA_LIVE_MAP[type]}
    >
      <div className="h-[3px] bg-gray-100 rounded-t-lg overflow-hidden">
        <div
          className={`h-full ${colors.progress}`}
          style={{
            animation: `toast-progress ${duration}ms linear forwards`,
            animationPlayState: isPaused ? 'paused' : 'running'
          }}
        />
      </div>

      <div className="flex items-center gap-3 px-4 py-3">
        <Icon size={20} className={`flex-shrink-0 ${colors.icon}`} />
        <span className="flex-1 text-sm font-medium text-gray-900">{message}</span>
        <button
          onClick={handleClose}
          className="flex-shrink-0 rounded p-1 transition-colors text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          aria-label="Fermer"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
